// "Flower garden": a sunlit childhood garden, small footsteps → tap a plant to learn its name → the haze of time → a path of light → the painting.
export const PLANTS = [
  { th: -2.5, type: 5, name: "mango tree", note: "its new leaves come out copper-red" },
  { th: -1.85, type: 0, name: "marigold", note: "it keeps the pests away from the garden" },
  { th: -1.2, type: 3, name: "bluebells", note: "they hang their heads like little bells" },
  { th: -0.6, type: 4, name: "roses", note: "every colour of rose says something different" },
  { th: 0, type: 2, name: "hibiscus", note: "each flower opens for just one day" },
  { th: 0.6, type: 1, name: "petunia", note: "it smells sweetest in the evening" },
  { th: 1.2, type: 6, name: "eggplant", note: "its flowers are soft purple stars" },
  { th: 1.85, type: 7, name: "dhatura", note: "a white trumpet that opens at night, never to be eaten" },
  { th: 2.5, type: 8, name: "lime tree", note: "even its leaves smell of lime" },
];

export const garden = {
  slug: "flower-garden",
  duration: 27,
  plants: PLANTS,
  captionTail: "I can't pass a flower without thinking of him.",
  stages: [
    { id: "garden", at: 0, hint: "a childhood garden" },
    { id: "names", at: 4.5, hint: "tap a plant to learn its name" },
    { id: "haze", at: 15, hint: "the haze of time" },
    { id: "bridge", at: 19, hint: "" },
  ],
  touch: { kind: "plant", from: 4.5, until: 19 },
  // painted panorama: the sunlit childhood garden, then the haze of time
  layers: [
    { src: "/arcs/garden/garden.webp", wrap: 3.48, th: 0, h: 8.4, y: 1.8, r: 4.9, mist: [[0, 0.1], [17, 0.1], [20, 0.4]], mistCol: "#f5e6c8", opacity: [[0, 1], [4.5, 0.75], [19, 0.75], [20, 1]], show: [-1, 0, 99, 99] },
  ],
  line: { text: "The past grows hazy, but the garden of memory never fades.", from: 15.5, to: 26.5, pos: "low" },
  ambient: { scene: "garden", until: 99, then: [[9, "summer"]] },
  look: { start: -0.12, lift: [0, 1], end: -0.12, yaw: [[0, 0], [4, 0], [7, -0.7], [10, 0.7], [13, 0], [27, 0]] },
  calm: { duration: 12, fade: [4, 8.5], line: [5.5, 11.5], backdrop: "garden", pos: "low" },
  speed: [[0, 1], [27, 1]],
  frag: `
uniform sampler2D uMap;
uniform vec4 uCrop;
uniform float uT, uAspect, uTileH, uRadius, uCy, uSurface;
uniform vec4 uPlant[9];
varying vec3 vPos;
${"__NOISE__"}
float hash2(vec2 p){ return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5453); }
vec3 tap(vec2 uv){
  vec2 m = 1.0 - abs(1.0 - mod(uv, 2.0));
  return texture2D(uMap, mix(uCrop.xy, uCrop.zw, m)).rgb;
}
float seg(vec2 p, vec2 a, vec2 b){ vec2 pa = p - a, ba = b - a; float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0); return length(pa - ba * h); }
float ink(float d, float w){ return 1.0 - smoothstep(w * 0.6, w, d); }
// one bloom by kind, p relative to its centre
vec4 bloom(vec2 p, float type, float k){
  float a = atan(p.y, p.x) + k;
  float d = length(p);
  if (type < 0.5) { float r = 0.15 * (0.82 + 0.18 * sin(a * 14.0)); return vec4(mix(vec3(0.8, 0.32, 0.02), vec3(1.0, 0.62, 0.1), d / r), step(d, r)); }
  if (type < 1.5) { float r = 0.13 * (0.72 + 0.28 * cos(a * 5.0)); return vec4(mix(vec3(0.98, 0.9, 0.96), vec3(0.72, 0.18, 0.62), smoothstep(0.0, 0.05, d)), step(d, r)); }
  if (type < 2.5) { float r = 0.21 * (0.45 + 0.55 * pow(abs(cos(a * 2.5)), 0.5)); float st = ink(seg(p, vec2(0.0), vec2(0.07, 0.17)), 0.018); return vec4(mix(mix(vec3(0.62, 0.04, 0.1), vec3(0.92, 0.14, 0.2), d / r), vec3(1.0, 0.85, 0.3), st), max(step(d, r), st)); }
  if (type < 3.5) { vec2 q = p + vec2(0.0, 0.04); float w = 0.06 * (1.0 + q.y * -4.0); float bell = step(abs(q.x), w) * step(-0.08, q.y) * step(q.y, 0.06) * step(abs(q.x), 0.08 + 0.02 * sin(q.x * 90.0)); return vec4(vec3(0.33, 0.45, 0.92), bell); }
  if (type < 4.5) { float r = 0.14; float sp = step(0.5, fract(a / 6.2832 + d * 14.0)); return vec4(mix(vec3(0.86, 0.12, 0.3), vec3(0.6, 0.04, 0.16), sp), step(d, r)); }
  if (type < 6.5) { float r = 0.07 * (0.7 + 0.3 * cos(a * 5.0)); return vec4(vec3(0.72, 0.6, 0.9), step(d, r)); }
  float r = 0.18 * (0.68 + 0.32 * pow(abs(cos(a * 2.5)), 3.0));
  return vec4(mix(vec3(0.85, 0.9, 0.8), vec3(0.99, 0.98, 0.95), d / r), step(d, r));
}
// a whole plant at p (x from its stem, y from the ground)
vec4 plant(vec2 p, float type, float fi){
  vec4 o = vec4(0.0);
  if (type > 4.5 && type < 5.5 || type > 7.5) {
    float tr = ink(abs(p.x + 0.05 * sin(p.y * 2.0)), 0.12) * step(p.y, 1.7);
    o = mix(o, vec4(0.4, 0.27, 0.18, 1.0), tr);
    vec2 c = (p - vec2(0.0, 2.1)) / vec2(0.95, 0.72);
    float can = step(length(c) + 0.22 * snoise(vec3(p * 2.5, fi)), 1.0);
    o = mix(o, vec4(mix(vec3(0.14, 0.4, 0.14), vec3(0.3, 0.56, 0.18), snoise(vec3(p * 4.0, fi)) * 0.5 + 0.5), 1.0), can);
    vec2 cell = floor(p / 0.3);
    vec2 fp = p - (cell + 0.5) * 0.3;
    float fh = hash2(cell + fi);
    float fr = type < 5.5 ? step(length(fp / vec2(0.06, 0.1)), 1.0) : step(length(fp / vec2(0.055, 0.045)), 1.0);
    vec3 fcol = type < 5.5 ? vec3(0.96, 0.7, 0.16) : vec3(0.62, 0.8, 0.22);
    o = mix(o, vec4(fcol, 1.0), fr * step(0.7, fh) * can);
    return o;
  }
  float h = 1.15;
  float stem = ink(abs(p.x + 0.04 * sin(p.y * 4.0)), 0.035) * step(p.y, h) * step(0.0, p.y);
  o = mix(o, vec4(0.2, 0.46, 0.16, 1.0), stem);
  for (int s = 0; s < 2; s++){
    float sg = float(s) * 2.0 - 1.0;
    vec2 lq = p - vec2(sg * 0.12, 0.35 + float(s) * 0.22);
    float leaf = step(length(lq / vec2(0.16, 0.06)), 1.0);
    o = mix(o, vec4(0.22, 0.52, 0.18, 1.0), leaf);
  }
  vec2 at[3];
  at[0] = vec2(0.0, h); at[1] = vec2(-0.26, h * 0.72); at[2] = vec2(0.26, h * 0.82);
  for (int j = 0; j < 3; j++){
    vec2 q = p - at[j];
    if (type > 5.5 && type < 6.5) {
      float fr = step(length((q + vec2(0.0, 0.16)) / vec2(0.08, 0.17)), 1.0);
      o = mix(o, vec4(0.34, 0.1, 0.4, 1.0), fr * step(0.5, float(j)));
    }
    o = mix(o, vec4(bloom(q, type, float(j) + fi).rgb, 1.0), bloom(q, type, float(j) + fi).a);
  }
  return o;
}
void main(){
  float n = snoise(vPos * 0.6) * 0.5 + 0.5;
  float n2 = snoise(vPos * 3.0 + 4.0) * 0.5 + 0.5;
  float th = atan(vPos.x, -vPos.z);
  float haze = smoothstep(15.0, 19.5, uT);
  float bridge = smoothstep(19.0, 22.0, uT);
  float settle = smoothstep(21.5, 26.5, uT);
  // the painting's strokes: crimson, maroon, yellow and green, thick and ridged
  vec3 strokes;
  {
    float s = snoise(vec3(vPos.x * 0.9 + vPos.z * 0.4, vPos.y * 2.6, vPos.z * 0.9)) * 0.5 + 0.5;
    float r = 0.85 + 0.2 * sin(vPos.y * 60.0 + s * 9.0);
    strokes = (s < 0.3 ? vec3(0.64, 0.19, 0.24) : s < 0.5 ? vec3(0.42, 0.1, 0.16) : s < 0.68 ? vec3(0.93, 0.77, 0.53) : vec3(0.23, 0.34, 0.32)) * r;
  }
  vec3 col;
  vec2 local;
  if (uSurface < 0.5) {
    float X = th * uRadius;
    float y = vPos.y;
    col = mix(vec3(0.78, 0.9, 0.98), vec3(0.5, 0.72, 0.95), smoothstep(2.0, 8.0, y));
    float hedge = 1.0 - smoothstep(-0.03, 0.03, y - (1.75 + 0.3 * snoise(vec3(X * 1.1, 0.0, 2.0)) + 0.08 * snoise(vec3(X * 5.0, 0.0, 3.0))));
    n2 = mix(n2, snoise(vec3(X * 4.0, y * 4.0, 6.0)) * 0.5 + 0.5, 0.6);
    col = mix(col, mix(vec3(0.2, 0.45, 0.16), vec3(0.34, 0.6, 0.2), n2), hedge);
    vec3 keep = vec3(0.0);
    float keepK = 0.0;
    float halo = 0.0;
    for (int i = 0; i < 9; i++){
      vec4 pl = uPlant[i];
      float dx = (th - pl.x) * uRadius;
      if (abs(dx) > 1.3) continue;
      vec4 o = plant(vec2(dx, y - 0.05), pl.y, float(i));
      col = mix(col, o.rgb, o.a);
      float g = pl.z > 0.0 ? smoothstep(pl.z, pl.z + 0.8, uT) : 0.0;
      keep = mix(keep, o.rgb, o.a * g);
      keepK = max(keepK, o.a * g);
      halo = max(halo, g * exp(-pow(length(vec2(dx, (y - 1.2) * 0.7)), 2.0) * 1.2));
    }
    col = mix(col, strokes, haze * 0.88);
    col = mix(col, keep * 1.1, keepK * 0.85);
    col += vec3(1.0, 0.88, 0.55) * halo * (0.12 + 0.25 * haze);
    // the bridge: a line of light along the ground joining the glowing plants
    col += vec3(1.0, 0.85, 0.5) * bridge * (1.0 - smoothstep(0.0, 0.18, abs(y - 0.25))) * 0.6 * step(0.001, halo + 0.001);
    local = vec2(X, y - uCy);
  } else if (uSurface < 1.5) {
    vec2 xz = vPos.xz;
    float path = 1.0 - smoothstep(0.45, 0.6, abs(xz.x + 0.2 * sin(xz.y * 0.5)));
    col = mix(mix(vec3(0.28, 0.55, 0.2), vec3(0.4, 0.66, 0.26), n), mix(vec3(0.82, 0.7, 0.52), vec3(0.88, 0.78, 0.6), n2), path);
    // small footsteps arriving along the path
    float stepZ = -4.5 + floor(uT / 0.6) * 0.42;
    vec2 cell = vec2(0.0, floor(xz.y / 0.42));
    float side = mod(cell.y, 2.0) * 2.0 - 1.0;
    vec2 fp = xz - vec2(side * 0.11, (cell.y + 0.5) * 0.42);
    float foot = step(length(fp / vec2(0.05, 0.1)), 1.0) + step(length((fp - vec2(0.0, -0.13)) / vec2(0.045, 0.04)), 1.0);
    col = mix(col, vec3(0.62, 0.5, 0.36), min(foot, 1.0) * step(xz.y, min(stepZ, 0.0)) * step(-4.6, xz.y) * path * 0.7);
    col = mix(col, strokes, haze * 0.75);
    col += vec3(1.0, 0.85, 0.5) * bridge * path * 0.35;
    local = xz;
  } else {
    col = mix(vec3(0.62, 0.8, 0.96), strokes, haze * 0.6);
    local = vPos.xz;
  }
  vec2 tile = vec2(uTileH * uAspect, uTileH);
  vec3 paint = tap(local / tile + 0.5);
  float drip = uSurface < 0.5 ? (10.0 - vPos.y) / 10.0 : 0.5 + length(vPos.xz) / (uRadius * 3.0);
  col = mix(col, paint, smoothstep(0.0, 0.25, settle * 1.3 - drip * 0.6 - n * 0.25));
  gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
  #include <colorspace_fragment>
}`,
};
